import React, { useState, useEffect, useMemo } from 'react';
import { AZURE } from '../../services/api';
import { useSport } from '../../contexts/SportContext';
import { BRAND_GREEN, BRAND_GOLD } from '../../theme/sucronTheme';

const TeamAthleteSelector = () => {
  const { activeSport } = useSport();
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(() => {
    try {
      return localStorage.getItem('arenastats_selected_team') || '';
    } catch {
      return '';
    }
  });
  const [selectedAthlete, setSelectedAthlete] = useState(() => {
    try {
      return localStorage.getItem('arenastats_selected_athlete') || '';
    } catch {
      return '';
    }
  });
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Fetch teams based on active sport (Brazilian clubs for swimming)
  useEffect(() => {
    const fetchTeams = async () => {
      if (activeSport !== 'swimming') {
        setTeams([]);
        setSelectedTeam('');
        setAthletes([]);
        return;
      }
      setLoading(true);
      try {
        // Fetch Brazilian clubs only (non-national-team + national team Brasil)
        const { data } = await AZURE.get('/swimming/clubs', {
          params: { limit: 500 }
        });
        // Backend returns { items: [...] }
        const allClubs = data.items || data.clubs || data.data || [];

        // Filter: Brazilian clubs only (country BRA or Brazil)
        const braClubs = allClubs.filter(c => {
          const country = (c.country || '').toUpperCase();
          return country === 'BRA' || country === 'BRAZIL' || country === 'BRASIL';
        });

        // Sort: national team first, then by total_athletes descending
        braClubs.sort((a, b) => {
          if (a.is_national_team && !b.is_national_team) return -1;
          if (!a.is_national_team && b.is_national_team) return 1;
          return (b.total_athletes || 0) - (a.total_athletes || 0);
        });

        console.log('[TeamAthleteSelector] ✅ Brazilian clubs loaded:', braClubs.length);
        setTeams(braClubs);
      } catch (error) {
        console.error('[TeamAthleteSelector] ❌ Error fetching clubs:', error);
        setTeams([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [activeSport]);

  // Fetch athletes when team is selected
  useEffect(() => {
    if (!selectedTeam) {
      setAthletes([]);
      return;
    }

    const fetchAthletes = async () => {
      setLoading(true);
      try {
        let endpoint = '';
        if (activeSport === 'swimming') {
          endpoint = `/swimming/clubs/${selectedTeam}/athletes`;
        } else if (activeSport === 'football') {
          endpoint = `/football/teams/${selectedTeam}/players`;
        }

        if (endpoint) {
          const { data } = await AZURE.get(endpoint);
          const athleteList = data.items || data.swimmers || data.athletes || data.players || data.data || [];
          setAthletes(athleteList.slice(0, 200));
        }
      } catch (error) {
        console.error('[TeamAthleteSelector] Error fetching athletes:', error);
        setAthletes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAthletes();
  }, [selectedTeam, activeSport]);

  // Filtered teams for search
  const filteredTeams = useMemo(() => {
    if (!searchTerm.trim()) return teams;
    const q = searchTerm.toLowerCase();
    return teams.filter(t => (t.name || '').toLowerCase().includes(q));
  }, [teams, searchTerm]);

  // Selected team object
  const selectedTeamObj = useMemo(() => {
    return teams.find(t => String(t.id) === String(selectedTeam));
  }, [teams, selectedTeam]);

  const handleTeamSelect = (teamId) => {
    console.log('[TeamAthleteSelector] 🎯 Team selected:', teamId);
    setSelectedTeam(String(teamId));
    setSelectedAthlete('');
    setShowDropdown(false);
    setSearchTerm('');
    try {
      localStorage.setItem('arenastats_selected_team', String(teamId));
      localStorage.removeItem('arenastats_selected_athlete');
    } catch (err) {
      console.error('[TeamAthleteSelector] ❌ localStorage error:', err);
    }
  };

  const handleClearTeam = () => {
    setSelectedTeam('');
    setSelectedAthlete('');
    setSearchTerm('');
    try {
      localStorage.removeItem('arenastats_selected_team');
      localStorage.removeItem('arenastats_selected_athlete');
    } catch {}
  };

  const handleAthleteChange = (e) => {
    const athleteId = e.target.value;
    setSelectedAthlete(athleteId);
    try {
      localStorage.setItem('arenastats_selected_athlete', athleteId);
    } catch {}
  };

  // Only show for swimming sport
  if (activeSport !== 'swimming') return null;

  return (
    <div className="px-4 py-3 space-y-3 relative">
      {/* Club Selector */}
      <div className="relative">
        <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: BRAND_GOLD }}>
          Clube · Natação BR
        </label>

        {/* Selected club display or search input */}
        {selectedTeamObj && !showDropdown ? (
          <div
            className="w-full flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white cursor-pointer hover:border-gray-400 transition-colors"
            onClick={() => setShowDropdown(true)}
          >
            {selectedTeamObj.logo_url && (
              <img
                src={selectedTeamObj.logo_url}
                alt=""
                className="w-5 h-5 rounded object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <span className="flex-1 truncate font-medium" style={{ color: BRAND_GREEN }}>
              {selectedTeamObj.name}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); handleClearTeam(); }}
              className="text-gray-400 hover:text-red-500 transition-colors"
              title="Limpar seleção"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              type="text"
              placeholder={loading ? 'Carregando clubes...' : `Buscar entre ${teams.length} clubes...`}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              disabled={loading}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/20 focus:border-gray-500 bg-white disabled:opacity-50"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full" />
              </div>
            )}
          </div>
        )}

        {/* Dropdown list */}
        {showDropdown && !loading && teams.length > 0 && (
          <>
            {/* Backdrop to close dropdown */}
            <div
              className="fixed inset-0 z-30"
              onClick={() => { setShowDropdown(false); setSearchTerm(''); }}
            />
            <div
              className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-40 max-h-64 overflow-y-auto"
            >
              {filteredTeams.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-gray-400">
                  Nenhum clube encontrado
                </div>
              ) : (
                filteredTeams.map((team) => {
                  const isSelected = String(team.id) === String(selectedTeam);
                  return (
                    <button
                      key={team.id}
                      onClick={() => handleTeamSelect(team.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                        isSelected
                          ? 'bg-gray-100 font-semibold'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {team.logo_url ? (
                        <img
                          src={team.logo_url}
                          alt=""
                          className="w-5 h-5 rounded object-contain flex-shrink-0"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div
                          className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: BRAND_GREEN }}
                        >
                          {(team.name || '?').substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <span className="flex-1 truncate" style={{ color: BRAND_GREEN }}>
                        {team.name}
                      </span>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">
                        {team.total_athletes || 0} atletas
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {/* Athlete Selector */}
      {selectedTeam && (
        <div className="relative z-10">
          <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: BRAND_GOLD }}>
            Nadador(a)
          </label>
          <select
            value={selectedAthlete}
            onChange={handleAthleteChange}
            disabled={loading || athletes.length === 0}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/20 focus:border-gray-500 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ WebkitAppearance: 'menulist', appearance: 'menulist' }}
          >
            <option value="">
              {loading ? 'Carregando...' : athletes.length === 0 ? 'Nenhum atleta' : `Selecionar (${athletes.length} atletas)`}
            </option>
            {athletes.map((athlete) => (
              <option key={athlete.id || athlete.swimmer_id} value={athlete.id || athlete.swimmer_id}>
                {athlete.name || athlete.full_name || athlete.athlete_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Context Confirmation */}
      {selectedTeamObj && (
        <div className="text-[10px] bg-gray-50 border border-gray-200 rounded-lg p-2">
          <p className="font-semibold mb-0.5" style={{ color: BRAND_GREEN }}>
            {selectedTeamObj.name}
          </p>
          <p className="text-gray-500">
            {selectedAthlete
              ? 'Clube e atleta selecionados — dados reais serão carregados.'
              : 'Selecione um nadador para visualizar dados individuais.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default TeamAthleteSelector;
